import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';

import { useAuthGuard } from '../../hooks/useAuthGuard';
import { Seo } from '../../components/seo/Seo';

const DRAWER_WIDTH = 240;

interface NavItem {
  label: string;
  to: string;
  match: string;
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Overview', to: '/dashboard', match: '/dashboard', end: true },
  { label: 'Alerts', to: '/dashboard/alerts', match: '/dashboard/alerts' },
  { label: 'Saved searches', to: '/dashboard/saved-searches', match: '/dashboard/saved-searches' },
  { label: 'Watchlist', to: '/dashboard/watchlist', match: '/dashboard/watchlist' },
  { label: 'Notifications', to: '/dashboard/notifications', match: '/dashboard/notifications' },
  { label: 'Privacy', to: '/dashboard/privacy', match: '/dashboard/privacy' },
  { label: 'Account', to: '/dashboard/account', match: '/dashboard/account' },
];

function isSelected(currentPath: string, item: NavItem): boolean {
  if (item.end) return currentPath === item.match;
  return currentPath.startsWith(item.match);
}

/**
 * Dashboard shell — sidebar nav + main pane.
 *
 * Protected by `useAuthGuard()`. If the user isn't signed in, the hook
 * redirects to `/login?return=<path>` and we render `null` while the
 * navigation flushes.
 */
export function DashboardLayout() {
  const { isAuthenticated } = useAuthGuard();
  const location = useLocation();

  if (!isAuthenticated) return null;

  return (
    <Box sx={{ display: 'flex', minHeight: '100%' }}>
      <Seo title="Dashboard" noindex />
      <Drawer
        variant="permanent"
        component="nav"
        aria-label="Dashboard sections"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            position: 'relative',
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar sx={{ minHeight: 0, py: 2 }}>
          <Typography variant="h2" component="p" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
            Your dashboard
          </Typography>
        </Toolbar>
        <List>
          {NAV_ITEMS.map((item) => (
            <ListItemButton
              key={item.to}
              component={RouterLink}
              to={item.to}
              selected={isSelected(location.pathname, item)}
              aria-current={isSelected(location.pathname, item) ? 'page' : undefined}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
      <Box component="section" sx={{ flex: 1, p: { xs: 2, md: 3 } }} aria-label="Dashboard content">
        <Outlet />
      </Box>
    </Box>
  );
}

export default DashboardLayout;
